import { useCallback, useEffect } from 'react';
import { useEmbeddingStore } from '../store';
import { usePostMessage } from './usePostMessage';
import type { EmbeddedService, EmbeddingMessageType } from '../../../types/embedding';

export function useEmbeddedService(service: EmbeddedService) {
  const { loadingStates, errorStates, setLoading, setError, registerService, unregisterService } =
    useEmbeddingStore();

  const { sendMessage: sendPostMessage } = usePostMessage();

  useEffect(() => {
    registerService(service);
    return () => unregisterService(service.id);
  }, [service, registerService, unregisterService]);

  const sendMessage = (
    target: Window | HTMLIFrameElement | null,
    type: EmbeddingMessageType,
    payload: unknown,
    targetOrigin?: string
  ) => {
    sendPostMessage(target, type, payload, targetOrigin);
  };

  const isLoading = loadingStates[service.id] ?? true;
  const error = errorStates[service.id] ?? null;

  const handleLoad = useCallback(() => setLoading(service.id, false), [service.id, setLoading]);
  const handleError = useCallback(
    (err: Error) => setError(service.id, err),
    [service.id, setError]
  );

  return {
    isLoading,
    error,
    sendMessage,
    onLoad: handleLoad,
    onError: handleError,
  };
}
